import { EventType } from "@/analytics/analytics-events";
import {
  ToggleClusterVisibilityProperties,
  ToggleScanVisibilityProperties,
} from "@/analytics/staging-area-workflows-events-properties";
import { setVisibilityForEntities } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-slice";
import {
  selectAffectedEntitiesOnVisibilityToggleFalse,
  selectAffectedEntitiesOnVisibilityToggleTrue,
  selectIsEntitySetToVisible,
} from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-visibility-selectors";
import { AppDispatch, RootState } from "@/store/store";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { FaroText, NonVisibleIcon, VisibleIcon } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
} from "@faro-lotv/service-wires";
import { ListItemIcon, MenuItem } from "@mui/material";
import { JSX } from "react";

type VisibilityToggleContextMenuActionItemProps = {
  disabled?: boolean;
  entity: CaptureTreeEntityRevision;
};

/**
 * @returns context menu item to toggle the visibility of entities.
 */
export function VisibilityToggleContextMenuActionItem({
  entity,
  disabled,
}: VisibilityToggleContextMenuActionItemProps): JSX.Element {
  const { getState, dispatch } = useAppStore();
  const isVisible = useAppSelector(selectIsEntitySetToVisible(entity.id));

  return (
    <MenuItem
      key="visibility"
      onClick={() => toggleVisibility(dispatch, getState(), isVisible, entity)}
      disabled={disabled}
    >
      <ListItemIcon>
        {isVisible ? <VisibleIcon /> : <NonVisibleIcon />}
      </ListItemIcon>
      <FaroText variant="labelL">{isVisible ? "Hide" : "Show"}</FaroText>
    </MenuItem>
  );
}

/**
 * Toggle the visibility of the given entity, also handling descendent entities.
 *
 * @param dispatch The dispatch function of the store.
 * @param state The current store state.
 * @param currentlyVisible Whether the entity is currently visible.
 * @param entity The entity to toggle the visibility of.
 */
export function toggleVisibility(
  dispatch: AppDispatch,
  state: RootState,
  currentlyVisible: boolean,
  entity: CaptureTreeEntityRevision,
): void {
  const newIsVisible = !currentlyVisible;

  if (entity.type === CaptureTreeEntityType.cluster) {
    Analytics.track<ToggleClusterVisibilityProperties>(
      EventType.toggleClusterVisibility,
      { visible: newIsVisible, origin: "staging area" },
    );
  } else {
    Analytics.track<ToggleScanVisibilityProperties>(
      EventType.toggleScanVisibility,
      {
        via: "scan tree",
        visible: newIsVisible,
        origin: "staging area",
      },
    );
  }

  const entityIds = newIsVisible
    ? selectAffectedEntitiesOnVisibilityToggleTrue(entity.id)(state)
    : selectAffectedEntitiesOnVisibilityToggleFalse(entity.id)(state);

  dispatch(
    setVisibilityForEntities({
      entityIds,
      isVisible: newIsVisible,
    }),
  );
}
